import { SolIcon } from '@sol/icons';
import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { AbstractComponent, FocusableComponent } from '../../abstract.component';
import { SolTextFieldComponent } from '../sol-text-field/sol-text-field.component';
import { debounceTime, Subject, Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SolSearchField } from '../../../models/core/sol-search-field';

@Component({
  selector: 'sol-search-field',
  imports: [SolTextFieldComponent],
  templateUrl: './sol-search-field.component.html',
  host: { role: 'search' },
})
export class SolSearchFieldComponent extends AbstractComponent<SolSearchField> implements AfterViewInit, OnDestroy {
  protected readonly SolIcon = SolIcon;

  // Computed
  protected readonly value$ = this.compute(state => state.value);
  protected readonly disabled$ = this.compute(state => state.disabled);
  protected readonly immediate$ = this.compute(state => state.immediate);
  protected readonly hasError$ = this.compute(state => state.hasError);
  protected readonly loading$ = this.compute(state => state.loading);

  // Events
  @Output('value')
  private readonly valueEmitter = new EventEmitter<string>();
  @Output('focused')
  public focusedEmitter = new EventEmitter<boolean>();

  // Binding
  @ViewChild(SolTextFieldComponent)
  private textFieldRef!: SolTextFieldComponent;

  // Cache
  private valueSubject = new Subject<string>();
  private valueSubscription?: Subscription;

  constructor() {
    super(SolSearchField.of());

    this.watchComputed(
      state => state.debounce,
      () => this.updateDebounce(),
    );
  }

  ngAfterViewInit(): void {
    this.updateDebounce();
  }

  ngOnDestroy(): void {
    this.valueSubscription?.unsubscribe();
  }

  @Input()
  public set value(value: string) {
    this.update(state => state.withValue(value));
  }

  @Input()
  public set disabled(disabled: boolean) {
    this.update(state => state.withDisabled(disabled));
  }

  @Input()
  public set hasError(hasError: boolean) {
    this.update(state => state.withHasError(hasError));
  }

  @Input()
  public set immediate(immediate: boolean) {
    this.update(state => state.withImmediate(immediate));
  }

  @Input()
  public set debounce(debounce: boolean) {
    this.update(state => state.withDebounce(debounce));
  }

  @Input()
  public set loading(loading: boolean) {
    this.update(state => state.withLoading(loading));
  }

  protected onSearch(value: string) {
    if (this.state.debounce) {
      this.valueSubject.next(value);
    } else {
      this.update(state => state.withValue(value));
      this.valueEmitter.emit(value);
    }
  }

  protected onSearchClear() {
    if (this.state.loading) {
      return;
    }
    this.onSearch('');
  }

  protected onFocused(focused: boolean) {
    this.focusedEmitter.emit(focused);
  }

  protected override focusable(): FocusableComponent | undefined {
    return this.textFieldRef;
  }

  private updateDebounce() {
    // Unsubscribe
    this.valueSubscription?.unsubscribe();
    this.valueSubscription = undefined;

    // Subscribe if needed
    const state = this.state;
    if (state.debounce) {
      this.valueSubscription = this.valueSubject
        .pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef))
        .subscribe(value => {
          this.update(state => state.withValue(value));
          this.valueEmitter.emit(value);
        });
    }
  }
}
